import { useMemo } from 'react';

import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl/components/text/Typography';

import Currency from 'components/currency';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { primitive, spacing } from 'styles/constants';
import { fontSizes } from 'styles/constants/fonts';

import { usePaymentContext } from '../../../../../../features/payment';
import { CardTypes, IPaymentMethod } from '../../../../../../features/payment/types';
import { mapPaymentMethodCardTypes } from '../../../../../../features/payment/utils';

const useAltPaymentMethodName = (paymentMethod: IPaymentMethod | undefined) => {
  const altPaymentMethodName = useMemo(() => {
    const altPaymentMethodsMap: { name: string; isSelected: boolean }[] = [
      {
        name: t('cash'),
        isSelected: !!paymentMethod?.cash,
      },
      {
        name: t('paypal'),
        isSelected: !!paymentMethod?.paypal,
      },
      {
        name: t('venmo'),
        isSelected: !!paymentMethod?.venmo,
      },
      {
        name: t('cashAppPay'),
        isSelected: !!paymentMethod?.cashapp,
      },
    ];

    const altPaymentMethod = altPaymentMethodsMap.find(
      altPaymentMethod => altPaymentMethod.isSelected
    );

    return altPaymentMethod?.name;
  }, [paymentMethod]);

  return altPaymentMethodName;
};

type PaymentMethodDetailsProps = {
  paymentMethod: IPaymentMethod | undefined;
  displayFor?: 'selection' | 'selectorOption';
};
export function PaymentMethodDetails({
  paymentMethod,
  displayFor = 'selection',
}: PaymentMethodDetailsProps) {
  const { getBalanceFromPaymentMethods } = usePaymentContext();
  const altPaymentMethodName = useAltPaymentMethodName(paymentMethod);
  const isDigitalGiftCardNumberExposedEnabled = useFlag(
    LaunchDarklyFlag.IS_DIGITAL_GIFT_CARD_NUMBER_EXPOSED_ENABLED
  );

  const isCard =
    !!paymentMethod?.credit &&
    paymentMethod?.credit?.cardType !== CardTypes.APPLE_PAY &&
    paymentMethod?.credit?.cardType !== CardTypes.GOOGLE_PAY;

  const isWallet =
    !!paymentMethod?.credit &&
    (paymentMethod?.credit?.cardType === CardTypes.APPLE_PAY ||
      paymentMethod?.credit?.cardType === CardTypes.GOOGLE_PAY);

  const cardType = mapPaymentMethodCardTypes(paymentMethod?.credit?.cardType);

  const containerStyle = StyleSheet.compose(styles.container, {
    flexDirection: displayFor === 'selection' ? 'column' : 'row',
    gap: displayFor === 'selection' ? 0 : 1,
    justifyContent: 'center',
  });

  const cardTypeStyle = StyleSheet.compose(styles.cardType, {
    fontFamily:
      displayFor === 'selection' ? primitive.fontFamily.$headers : primitive.fontFamily.$body,
    fontSize: displayFor === 'selection' ? 18 : 15,
    fontWeight: displayFor === 'selection' ? 'normal' : 'light',
    lineHeight: displayFor === 'selection' ? spacing.$6 : spacing.$5,
  });

  let alias = paymentMethod?.credit?.alias;
  if (paymentMethod?.credit?.cardType === CardTypes.APPLE_PAY) {
    alias = t('applePay');
  }
  if (paymentMethod?.credit?.cardType === CardTypes.GOOGLE_PAY) {
    alias = t('googlePay');
  }

  if (isCard) {
    return (
      <View style={containerStyle}>
        <View style={styles.selectionTextRow}>
          <Typography style={cardTypeStyle} testID="payment-method-card">
            {cardType || t('card')}
          </Typography>
          <Typography style={cardTypeStyle} testID="payment-method-alias">
            {t('endsInRedesign').toLowerCase()}
            {paymentMethod?.credit?.alias}
          </Typography>
        </View>
        {displayFor === 'selectorOption' && <Typography style={styles.cardExp}>-</Typography>}
        <Typography style={styles.cardExp}>
          {`${t('expire')} ${paymentMethod?.credit?.expiryMonth}/${
            paymentMethod?.credit?.expiryYear
          }`}
        </Typography>
      </View>
    );
  }

  if (isWallet) {
    return (
      <View style={containerStyle}>
        <Typography style={cardTypeStyle} testID="payment-method-card">
          {alias}
        </Typography>
      </View>
    );
  }

  if (paymentMethod?.prepaid) {
    const cardNumber = paymentMethod.prepaid.cardNumber;
    const lastFour = cardNumber ? cardNumber.slice(-4) : '';

    return (
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Typography
          fontFamily={displayFor === 'selection' ? 'heading' : 'body'}
          style={{
            fontSize: fontSizes.md,
            marginRight: spacing.$1,
          }}
        >
          {t('giftCard')}
          {isDigitalGiftCardNumberExposedEnabled ? `${lastFour ? ` *${lastFour}` : ''} -` : ''}
        </Typography>
        <Currency
          amount={getBalanceFromPaymentMethods(paymentMethod)}
          textProps={{
            style: [{ margin: 0 }, cardTypeStyle],
          }}
        />
      </View>
    );
  }

  // If we reached to this point we will be displaying an alternative payment method
  return (
    <View style={containerStyle}>
      <Typography style={cardTypeStyle}>{altPaymentMethodName}</Typography>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-start',
    flexShrink: 1,
    maxWidth: '100%',
    minWidth: 0,
  },
  cardContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.$1,
  },
  cardType: {
    color: primitive.$bbqBrown,
    fontSize: 18,
    fontWeight: 'normal',
  },
  selectionTextRow: {
    flexDirection: 'row',
    flexShrink: 1,
    flexWrap: 'wrap',
    gap: spacing.$1,
    maxWidth: '100%',
    minWidth: 0,
  },
  cardAlias: {
    fontFamily: primitive.fontFamily.$body,
    color: primitive.$bbqBrown,
    fontSize: primitive.fontSizes.md,
    marginLeft: spacing.$1,
  },
  cardExp: {
    fontFamily: primitive.fontFamily.$body,
    color: primitive.$bbqBrown,
    fontSize: primitive.fontSizes.md,
    textTransform: 'capitalize',
  },
});
